import { useState } from 'react';
import { NavArrowDownSolid } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'components/core/theme-provider';
import knowledgeIllustration from 'assets/images/knowledge-upload-illustration.png';
import { SettingsGroup, SettingsItem } from 'components/blocks/settings-drawer';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { cn } from 'lib/utils';
import type { ITool } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import type {
  Agent,
  AgentAttachedTool,
} from 'features/chat/agent-chat-module/types/agent.service.type';
import { useAgentChatModels } from 'features/chat/hooks/use-agent-chat-models';
import type { useAgentConfigSettings } from 'features/chat/hooks/use-agent-config-settings';
import { AgentConfigAddSkillModal } from 'pages/agents/components/agent-config-add-skill-modal';
import { AgentConfigAddToolModal } from 'pages/agents/components/agent-config-add-tool-modal';
import { AgentConfigSectionActions } from 'pages/agents/components/agent-config-section-actions';
import {
  AgentModelPicker,
  type ModelSelectionValue,
} from 'pages/agents/components/agent-model-picker';
import { AgentConfigAddKBModal } from 'pages/agents/components/knowledge-base-section/agents-config-add-kb-modal';
import { AgentConfigFolderList } from 'pages/agents/components/knowledge-base-section/agents-config-folder-list';
import { SkillsSection } from 'pages/agents/components/skills-section';
import { LearnedSkillsPanel } from 'pages/skills/learned-skills-panel';
import { ToolsSection } from 'pages/agents/components/tools-section';
import type { AgentConfigValues } from 'pages/agents/utils/agent-config-payload';
import {
  canEditAgentConfig,
  canManageKb,
  canManageSkills,
  canManageTools,
} from 'pages/agents/utils/agent-share-access';
import { useCommonStore } from 'state/store/common/useCommonStore';

// Hides the Select's built-in chevron; the design uses a filled caret instead.
// Figma: grey 8px field with 12px padding, no border.
const modelTriggerClassName =
  'min-h-0 rounded-field border-0 bg-grey-50 p-3 hover:bg-grey-100 dark:bg-white/10 dark:hover:bg-white/5';

const selectTriggerClassName =
  'h-auto w-40 gap-2 rounded-lg border-0 bg-grey-50 p-3 text-sm font-normal leading-heading text-grey-900 shadow-none focus:ring-0 dark:bg-white/10 [&>svg:last-child]:hidden';

type ConfigSectionProps = {
  canManage: boolean;
  isEnabled: boolean;
  onEnabledChange: (checked: boolean) => void;
};

type AgentConfigSettingsProps = {
  agent: Agent;
  projectKey: string;
  /** Shared with the Skills tab, so saving one tab never sends an old value for the other. */
  config: ReturnType<typeof useAgentConfigSettings>;
};

/** Model, Knowledge Base and Integrations, with the same rules and saving as the configuration page. */
export function AgentConfigSettings({ agent, projectKey, config }: AgentConfigSettingsProps) {
  const userId = useCommonStore((state) => state.user?.id);
  const { values, setModel, setSectionEnabled, addFolder, removeFolder, addTool, removeTool } =
    config;

  return (
    <>
      <ModelSettings
        values={values}
        disabled={!canEditAgentConfig(agent, userId)}
        onModelChange={setModel}
      />
      <KnowledgeBaseSettings
        projectKey={projectKey}
        folderIds={values.folderIds}
        canManage={canManageKb(agent, userId)}
        isEnabled={values.sectionState['knowledge-base']}
        onEnabledChange={(checked) => setSectionEnabled('knowledge-base', checked)}
        onAddFolder={addFolder}
        onRemoveFolder={removeFolder}
      />
      <IntegrationSettings
        toolIds={values.toolIds}
        attachedTools={agent.tools}
        canManage={canManageTools(agent, userId)}
        isEnabled={values.sectionState.tools}
        onEnabledChange={(checked) => setSectionEnabled('tools', checked)}
        onAddTool={addTool}
        onRemoveTool={removeTool}
      />
    </>
  );
}

type ModelSettingsProps = {
  values: AgentConfigValues;
  disabled: boolean;
  onModelChange: (model: ModelSelectionValue) => void;
};

function ModelSettings({ values, disabled, onModelChange }: ModelSettingsProps) {
  const { t } = useTranslation();
  const { availableChatModels, availableCustomChatModels, isLoadingLlmModels } =
    useAgentChatModels();

  return (
    <SettingsGroup className="gap-3">
      <SettingsItem
        title={t('MODEL', { defaultValue: 'Model' })}
        description={t('SELECT_MODEL_FOR_AGENT', {
          defaultValue: 'Select the model that should power your agent.',
        })}
      />
      <AgentModelPicker
        isLoadingLlmModels={isLoadingLlmModels}
        availableChatModels={availableChatModels}
        availableCustomChatModels={availableCustomChatModels}
        currentLlmModel={values.llmModel}
        currentLlmProvider={values.llmProvider}
        currentLlmModelId={values.llmModelId}
        isBlocksProvider={values.isBlocksProvider}
        onModelChange={onModelChange}
        disabled={disabled}
        triggerClassName={modelTriggerClassName}
      />
      {/* Layout only for now: there is no API for this setting yet. */}
      {/* <SettingsItem
        title={t('WHEN_LIMITS_ARE_REACHED', { defaultValue: 'When limits are reached' })}
        description={t('CHOOSE_WHAT_HAPPENS_AFTER_USAGE_LIMIT', {
          defaultValue: 'Choose what happens after running into a usage limit.',
        })}
        action={
          <Select defaultValue="switch" disabled={disabled}>
            <SelectTrigger className={selectTriggerClassName}>
              <SelectValue />
              <NavArrowDownSolid width={16} height={16} className="shrink-0 text-grey-500" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="switch">
                {t('SWITCH_MODEL', { defaultValue: 'Switch model' })}
              </SelectItem>
            </SelectContent>
          </Select>
        }
      /> */}
    </SettingsGroup>
  );
}

type KnowledgeBaseSettingsProps = ConfigSectionProps & {
  projectKey: string;
  folderIds: string[];
  onAddFolder: (folderId: string) => void;
  onRemoveFolder: (folderId: string) => void;
};

function KnowledgeBaseSettings({
  projectKey,
  folderIds,
  canManage,
  isEnabled,
  onEnabledChange,
  onAddFolder,
  onRemoveFolder,
}: KnowledgeBaseSettingsProps) {
  const { t } = useTranslation();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const title = t('KNOWLEDGE_BASE', { defaultValue: 'Knowledge Base' });

  return (
    <SettingsGroup>
      <SettingsItem
        title={title}
        description={t('ATTACH_FILES_AS_KNOWLEDGE', {
          defaultValue: 'Directly attach files to the project and all its chats as knowledge.',
        })}
        action={
          <AgentConfigSectionActions
            addLabel={t('ADD', { defaultValue: 'Add' })}
            onAdd={() => setIsAddModalOpen(true)}
            isAddDisabled={!canManage || !isEnabled}
            toggleLabel={title}
            checked={isEnabled}
            onCheckedChange={onEnabledChange}
            isToggleDisabled={!canManage}
          />
        }
      />
      <AgentConfigFolderList
        projectKey={projectKey}
        selectedFolderIds={folderIds}
        onRemove={onRemoveFolder}
        disabled={!canManage || !isEnabled}
        emptyState={
          <div className="flex h-[171px] items-center justify-center rounded-field border border-dashed border-grey-100 bg-grey-50 dark:border-border dark:bg-muted/30">
            <img src={knowledgeIllustration} alt="" className="h-28 w-[181px] object-cover" />
          </div>
        }
      />
      <AgentConfigAddKBModal
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
        projectKey={projectKey}
        currentFolderIds={folderIds}
        onAddKB={onAddFolder}
        onRemoveKB={onRemoveFolder}
      />
    </SettingsGroup>
  );
}

type IntegrationSettingsProps = ConfigSectionProps & {
  toolIds: string[];
  attachedTools?: AgentAttachedTool[];
  onAddTool: (tool: ITool) => void;
  onRemoveTool: (toolId: string) => void;
};

function IntegrationSettings({
  toolIds,
  attachedTools,
  canManage,
  isEnabled,
  onEnabledChange,
  onAddTool,
  onRemoveTool,
}: IntegrationSettingsProps) {
  const { t } = useTranslation();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  // Same title key as the configuration page's Tools section.
  const title = t('TOOLS', { defaultValue: 'Tools' });

  return (
    <SettingsGroup>
      <SettingsItem
        title={title}
        description={t('AGENT_CONFIG_SECTION_TOOLS_CONTENT', {
          defaultValue: 'Attach external tools and actions the agent can use when needed.',
        })}
        action={
          <AgentConfigSectionActions
            addLabel={t('ADD', { defaultValue: 'Add' })}
            onAdd={() => setIsAddModalOpen(true)}
            isAddDisabled={!canManage || !isEnabled}
            toggleLabel={title}
            checked={isEnabled}
            onCheckedChange={onEnabledChange}
            isToggleDisabled={!canManage}
          />
        }
      />
      <ToolsSection
        enabled={canManage && isEnabled}
        canEdit={canManage}
        selectedToolIds={toolIds}
        attachedTools={attachedTools}
        onRemoveTool={onRemoveTool}
      />
      <AgentConfigAddToolModal
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
        selectedToolIds={toolIds}
        onAddTool={onAddTool}
        onRemoveTool={onRemoveTool}
      />
    </SettingsGroup>
  );
}

type AgentSkillSettingsProps = {
  agent: Agent;
  config: ReturnType<typeof useAgentConfigSettings>;
};

/** Skills, with the same rules and saving as the configuration page. */
export function AgentSkillSettings({ agent, config }: AgentSkillSettingsProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const userId = useCommonStore((state) => state.user?.id);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [skillView, setSkillView] = useState<'attached' | 'learned'>('attached');
  const { values, setSectionEnabled, addSkill, removeSkill } = config;
  const canManage = canManageSkills(agent, userId);
  const isEnabled = values.sectionState.skills;
  const title = t('SKILLS', { defaultValue: 'Skills' });
  const activeText = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  const idleText = theme === 'dark' ? 'text-true-white/75' : 'text-grey-500';
  const skillViews = [
    { value: 'attached' as const, label: title },
    { value: 'learned' as const, label: t('LEARN_SKILLS', { defaultValue: 'Learn skills' }) },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div
        role="group"
        aria-label={t('FILTER', { defaultValue: 'Filter' })}
        className="flex h-10 w-full min-w-0 items-center rounded-lg border border-grey-100 bg-white p-1 sm:inline-flex sm:w-auto sm:self-start dark:border-border dark:bg-card"
      >
        {skillViews.map((option) => {
          const isActive = skillView === option.value;

          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={isActive}
              onClick={() => setSkillView(option.value)}
              className={cn(
                'h-8 min-w-0 flex-1 truncate rounded-md px-3 text-sm font-normal leading-heading transition-colors sm:flex-none',
                isActive ? `bg-grey-50 dark:bg-true-white/15 ${activeText}` : idleText
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {skillView === 'learned' ? (
        <LearnedSkillsPanel agentId={agent.id} agentName={agent.name || ''} />
      ) : (
        <SettingsGroup>
          <SettingsItem
            description={t('AGENT_CONFIG_SECTION_SKILLS_CONTENT', {
              defaultValue: 'Attach standing skills the agent can use across conversations.',
            })}
            action={
              <AgentConfigSectionActions
                addLabel={t('ADD', { defaultValue: 'Add' })}
                onAdd={() => setIsAddModalOpen(true)}
                isAddDisabled={!canManage || !isEnabled}
                toggleLabel={title}
                checked={isEnabled}
                onCheckedChange={(checked) => setSectionEnabled('skills', checked)}
                isToggleDisabled={!canManage}
              />
            }
          />
          <SkillsSection
            enabled={canManage && isEnabled}
            canEdit={canManage}
            selectedSkillIds={values.skillIds}
            onRemoveSkill={removeSkill}
          />
          <AgentConfigAddSkillModal
            open={isAddModalOpen}
            onOpenChange={setIsAddModalOpen}
            selectedSkillIds={values.skillIds}
            onAddSkill={(skill) => addSkill(skill.id)}
            onRemoveSkill={removeSkill}
          />
        </SettingsGroup>
      )}
    </div>
  );
}
